import {
  differenceInMilliseconds,
  differenceInSeconds,
  differenceInMinutes,
  differenceInHours,
  format as formatDate,
  parseISO,
  addDays,
} from 'date-fns'
import {toZonedTime} from 'date-fns-tz'
import {
  DATE_AT_TIME_12H_FORMAT,
  DATE_DAY_MONTH_YEAR_FORMAT,
  DAY_IN_MILLISECONDS,
} from 'definitions/constant'
import {pluralizeText} from 'utils'

const monthAsMilliseconds = differenceInMilliseconds(
  addDays(new Date(), 30),
  new Date(),
)
const secondsInAYear = 31536000
const secondsInAMonth = 2592000
const secondsInADay = 86400
const secondsInAnHour = 3600
const secondsInAMinute = 60

export function getTimeAgo(targetDate: string): string {
  if (!targetDate) return 'Invalid Date'

  const date = new Date(targetDate)
  const currentDate = new Date()

  const timeDifference = differenceInMilliseconds(currentDate, date)

  if (timeDifference >= monthAsMilliseconds) {
    return formatDate(date, DATE_DAY_MONTH_YEAR_FORMAT)
  }

  return timeSince(date)
}

export function timeSince(date: Date) {
  const seconds = Math.floor(differenceInSeconds(new Date(), date))

  let interval = seconds / secondsInAYear
  if (interval >= 1) {
    return Math.floor(interval) + ` ${pluralizeText('year', interval)} ago`
  }
  interval = seconds / secondsInAMonth
  if (interval >= 1) {
    return Math.floor(interval) + ` ${pluralizeText('month', interval)} ago`
  }
  interval = seconds / secondsInADay
  if (interval >= 1) {
    return Math.floor(interval) + ` ${pluralizeText('day', interval)} ago`
  }
  interval = seconds / secondsInAnHour
  if (interval >= 1) {
    return Math.floor(interval) + ` ${pluralizeText('hr', interval)} ago`
  }
  interval = seconds / secondsInAMinute
  if (interval >= 1) {
    return Math.floor(interval) + ` ${pluralizeText('min', interval)} ago`
  }
  return Math.floor(seconds) + ` ${pluralizeText('sec', interval)} ago`
}

export const getDayMonthStringYearFormat = (date: string): string =>
  formatDate(toZonedTime(date, 'UTC'), DATE_DAY_MONTH_YEAR_FORMAT)

export const transformToDateAtTimeString = (
  date: string,
  isDateInUTC: boolean = false,
): string => {
  if (!date) {
    return ''
  }

  const parsedISODate = parseISO(date)

  const timezonedDate = isDateInUTC
    ? parsedISODate
    : toZonedTime(parsedISODate, 'UTC')
  const now = toZonedTime(new Date(), 'UTC')

  if (timezonedDate > now) {
    return 'just now'
  }

  const hoursDiff = differenceInHours(now, timezonedDate)
  const minutesDiff = differenceInMinutes(now, timezonedDate)

  if (hoursDiff === 0) {
    return minutesDiff < 1
      ? 'just now'
      : minutesDiff + (minutesDiff > 1 ? ' minutes ago' : ' minute ago')
  }
  if (hoursDiff <= 24) {
    return hoursDiff + (hoursDiff > 1 ? ' hours ago' : ' hour ago')
  }
  return formatDate(timezonedDate, DATE_AT_TIME_12H_FORMAT)
}

export const getDurationInMilliseconds = (days: number): number =>
  days * DAY_IN_MILLISECONDS
